import api from './client';
import type { ImportFeilRad, ImportForhandsvisning } from './types';

/** Feil fra import-endepunktene (400/409/413) med serverens feilliste. */
export class ImportAPIFeil extends Error {
  status: number;
  feil: ImportFeilRad[];

  constructor(status: number, feil: ImportFeilRad[]) {
    super(feil[0]?.melding ?? 'Import feilet');
    this.name = 'ImportAPIFeil';
    this.status = status;
    this.feil = feil;
  }
}

/** Malen serveren beskriver; nettleseren bygger .xlsx av den. */
export interface ImportMal {
  overskrifter: string[];
  eksempelrader: string[][];
}

export interface ImportAPI<R, Res> {
  forhandsvis(csv: string, filnavn: string): Promise<ImportForhandsvisning<R>>;
  bekreft(csv: string, filnavn: string): Promise<Res>;
  hentMal(): Promise<ImportMal>;
}

const FEILTEKST: Record<number, string> = {
  413: 'Filen er for stor (maks 2 MB).',
  415: 'Serveren tok ikke imot CSV-teksten.',
};

const tilImportFeil = (err: unknown): unknown => {
  const e = err as { response?: { status?: number; data?: unknown } };
  const status = e?.response?.status;
  if (status !== 400 && status !== 409 && status !== 413 && status !== 415) return err;
  const data = e.response?.data as { feil?: unknown } | undefined;
  const liste = data?.feil;
  const feil = Array.isArray(liste)
    ? (liste as ImportFeilRad[]).filter((f) => f && typeof f.melding === 'string')
    : [];
  return new ImportAPIFeil(status, feil.length > 0 ? feil : [{ melding: FEILTEKST[status] ?? 'Importen ble avvist.' }]);
};

const csvInnstillinger = (filnavn: string) => ({
  headers: { 'Content-Type': 'text/csv', 'X-Import-Filnavn': encodeURIComponent(filnavn) },
});

/** Lager et API-objekt for et importendepunkt, f.eks. lagImportAPI('/talent/import'). */
export const lagImportAPI = <R, Res>(basePath: string): ImportAPI<R, Res> => ({
  async forhandsvis(csv, filnavn) {
    try {
      const response = await api.post(`${basePath}/forhandsvis`, csv, csvInnstillinger(filnavn));
      return response.data;
    } catch (err) {
      throw tilImportFeil(err);
    }
  },

  async bekreft(csv, filnavn) {
    try {
      const response = await api.post(basePath, csv, csvInnstillinger(filnavn));
      return response.data;
    } catch (err) {
      throw tilImportFeil(err);
    }
  },

  async hentMal() {
    const response = await api.get(`${basePath}/mal`);
    return response.data;
  },
});
